import type { Priorite } from '../partage/api/types';
import type { Calque } from './calque';

/**
 * Le filtre de la Carte (chantier `revue-design`, ticket 04, maquette `design/carte.html`,
 * décisions d'Igor du 2026-10-03, « La Carte » 1 et 2) : une importance choisie dans les
 * pastilles (Tout, Urgent, Important, Quand tu veux). Il remplace le calque qu'on prenait
 * dans ⚙ : rien ne s'ombre plus. **Aux profondeurs 2 et 3, les bulles concernées grossissent
 * et les autres rétrécissent ; au niveau 1, rien ne change de taille.** Le choix lui-même est
 * tenu par `useCalque` (`calqueDuDossier.ts`), le même qu'en Hiérarchie.
 *
 * Le serveur place la carte avec ces tailles (chantier `flottaison-suite`, ticket 08) : le
 * client lui envoie l'importance et la profondeur, puis dessine chaque bulle à la taille avec
 * laquelle elle a été placée. Les nombres sont ceux de `server/src/domain/carte/reglagesDeLaCarte.ts`,
 * recopiés ici parce que le client ne lit pas le serveur ; `tests/client/carte/filtreDeLaCarte.test.ts`
 * vérifie qu'ils restent les mêmes. Règles pures, sans rendu.
 */

/** La taille d'une bulle concernée : elle, ou une bulle qu'elle contient, a l'importance. */
export const TAILLE_CONCERNEE = 1.18;
/** La taille des autres. */
export const TAILLE_AUTRE = 0.72;
/** À partir de cette profondeur, les tailles comptent. */
export const PROFONDEUR_DU_FILTRE = 2;

export interface FiltreDeLaCarte {
  importance: Priorite;
  profondeur: number;
}

/** Ce que la carte envoie au serveur : rien au niveau 1, ni sans importance choisie. */
export function filtreEnvoye(enAvant: Priorite | null, profondeur: number): FiltreDeLaCarte | null {
  if (enAvant === null || profondeur < PROFONDEUR_DU_FILTRE) return null;
  return { importance: enAvant, profondeur };
}

/** La taille d'une bulle à l'écran : 1 sans filtre envoyé. `concernee` : `dansLeCalque(...).allumee`. */
export function tailleAuFiltre(concernee: boolean, filtre: FiltreDeLaCarte | null): number {
  if (filtre === null) return 1;
  return concernee ? TAILLE_CONCERNEE : TAILLE_AUTRE;
}

/**
 * Ce que compte la phrase du filtre : combien de bulles de cette importance la feuille porte,
 * et dans combien de branches (les bulles du premier tour qui en portent). Une bulle du premier
 * tour que la carte n'a pas comptée se juge sur sa propre importance, comme `dansLeCalque`.
 */
export function compterLeFiltre(
  premierTour: readonly { id: string; priorite: { retenue: Priorite } | null }[],
  enAvant: Priorite,
  calque: Calque
): { combien: number; branches: number } {
  let combien = 0;
  let branches = 0;
  for (const bulle of premierTour) {
    const compte = calque[bulle.id];
    const ici = compte ? (compte[enAvant] ?? 0) : bulle.priorite?.retenue === enAvant ? 1 : 0;
    combien += ici;
    if (ici > 0) branches += 1;
  }
  return { combien, branches };
}
